import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import axios from 'axios'

// Facial Recognition Microservice API URL
const FACE_API_URL = import.meta.env.VITE_FACE_API_URL || 'http://localhost:8001'

// Helper to parse FastAPI exception responses
const parseError = (err, fallbackType, fallbackMessage) => {
  const errorData = err.response?.data || {}
  const detail = errorData.detail || {}
  return {
    type: detail.error || errorData.error || fallbackType,
    message: detail.message || errorData.message || fallbackMessage,
    details: detail.details || errorData.details || err.message,
  }
}

export const useFaceRecognitionStore = defineStore('faceRecognition', () => {
  const authStore = useAuthStore()

  // State
  const loading = ref(false)
  const error = ref(null)
  const cameraStream = ref(null)
  const capturedImage = ref(null)
  const verificationResult = ref(null)
  const registrationResult = ref(null)

  // Computed
  const isProcessing = computed(() => loading.value)
  const hasError = computed(() => !!error.value)

  // Helper: Convert canvas to base64
  const canvasToBase64 = (canvas) => {
    return canvas.toDataURL('image/jpeg', 0.9)
  }

  // Helper: Capture image from video element
  const captureFromVideo = (videoElement) => {
    const canvas = document.createElement('canvas')
    canvas.width = videoElement.videoWidth
    canvas.height = videoElement.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
    return canvasToBase64(canvas)
  }

  // Start camera stream
  const startCamera = async () => {
    try {
      error.value = null

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
      })

      cameraStream.value = stream
      return stream
    } catch (err) {
      error.value = {
        type: 'CameraAccessError',
        message: 'Failed to access camera. Please grant camera permissions.',
        details: err.message,
      }
      throw err
    }
  }

  // Stop camera stream
  const stopCamera = () => {
    if (cameraStream.value) {
      cameraStream.value.getTracks().forEach((track) => track.stop())
      cameraStream.value = null
    }
  }

  // Capture image from camera
  const captureImage = (videoElement) => {
    try {
      if (!videoElement) {
        throw new Error('Video element not provided')
      }

      capturedImage.value = captureFromVideo(videoElement)
      return capturedImage.value
    } catch (err) {
      error.value = {
        type: 'CaptureError',
        message: 'Failed to capture image',
        details: err.message,
      }
      throw err
    }
  }

  // Register face (Employer side)
  const registerFace = async (employeeId, companyId, imageBase64, metadata = null) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.post(`${FACE_API_URL}/registration/register`, {
        employee_id: employeeId,
        company_id: companyId,
        image_base64: imageBase64,
        metadata: metadata,
      })

      registrationResult.value = response.data

      // Update user profile in auth store
      if (employeeId === authStore.user?.uid) {
        authStore.userProfile.hasFaceRegistered = true
      }

      return response.data
    } catch (err) {
      error.value = parseError(err, 'RegistrationError', 'Failed to register face')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // Verify face (Employee side - test verification before attendance)
  const verifyFace = async (employeeId, imageBase64) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.post(`${FACE_API_URL}/attendance/verify`, {
        employee_id: employeeId,
        image_base64: imageBase64,
      })

      verificationResult.value = response.data
      return response.data
    } catch (err) {
      error.value = parseError(err, 'VerificationError', 'Failed to verify face')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // Record attendance with face verification (Employee side)
  const recordAttendance = async (employeeId, companyId, attendanceType, imageBase64, location = null) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.post(`${FACE_API_URL}/attendance/record`, {
        employee_id: employeeId,
        company_id: companyId,
        attendance_type: attendanceType,
        image_base64: imageBase64,
        location: location,
      })

      return response.data
    } catch (err) {
      error.value = parseError(err, 'AttendanceError', 'Failed to record attendance')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // Update face registration (Employer side)
  const updateFace = async (employeeId, imageBase64, metadata = null) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.put(`${FACE_API_URL}/registration/update`, {
        employee_id: employeeId,
        image_base64: imageBase64,
        metadata: metadata,
      })

      return response.data
    } catch (err) {
      error.value = parseError(err, 'UpdateError', 'Failed to update face')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // Delete face registration (Employer side)
  const deleteFace = async (employeeId) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.delete(`${FACE_API_URL}/registration/${employeeId}`)

      // Update user profile in auth store
      if (employeeId === authStore.user?.uid) {
        authStore.userProfile.hasFaceRegistered = false
      }

      return response.data
    } catch (err) {
      error.value = parseError(err, 'DeleteError', 'Failed to delete face')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // List registered faces for company (Employer side)
  const listRegisteredFaces = async (companyId) => {
    try {
      loading.value = true
      error.value = null

      const response = await axios.get(`${FACE_API_URL}/registration/list/${companyId}`)

      return response.data
    } catch (err) {
      error.value = parseError(err, 'ListError', 'Failed to list registered faces')
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // Get model info
  const getModelInfo = async () => {
    try {
      const response = await axios.get(`${FACE_API_URL}/model-info`)
      return response.data
    } catch (err) {
      console.error('Failed to get model info:', err)
      return null
    }
  }

  // Check service health
  const checkHealth = async () => {
    try {
      const response = await axios.get(`${FACE_API_URL}/health`)
      return response.data
    } catch (err) {
      console.error('Face recognition service is unavailable:', err)
      return null
    }
  }

  // Clear error
  const clearError = () => {
    error.value = null
  }

  // Reset state
  const reset = () => {
    stopCamera()
    loading.value = false
    error.value = null
    capturedImage.value = null
    verificationResult.value = null
    registrationResult.value = null
  }

  return {
    // State
    loading,
    error,
    cameraStream,
    capturedImage,
    verificationResult,
    registrationResult,

    // Computed
    isProcessing,
    hasError,

    // Actions
    startCamera,
    stopCamera,
    captureImage,
    registerFace,
    verifyFace,
    recordAttendance,
    updateFace,
    deleteFace,
    listRegisteredFaces,
    getModelInfo,
    checkHealth,
    clearError,
    reset,
  }
})
